// ABOUTME: Catalog view for prototype selection using the ModelSelection component.
// ABOUTME: Wraps ModelSelectionProvider with data from useModelSelectionData and handles product click.

import type {
    ICategory,
    IFilter,
    IModelSelectionContext,
    IModelSelectionData,
    IModelSelectionSettings,
    IMSHooks,
    IProduct,
} from '@coolshop/cool-model-selection';
import {
    CompareProductsProvider,
    ModelSelection,
    ModelSelectionProvider,
    useModelSelection,
} from '@coolshop/cool-model-selection';
import { MouseEvent, MutableRefObject, useEffect, useMemo, useRef } from 'react';

import { SoonAvailabilityBadge } from './SoonAvailabilityBadge';

interface CatalogViewProps {
    data: IModelSelectionData;
    onProductClick: (product: IProduct) => void;
    loadSubranges: (params: { range: ICategory }) => Promise<ICategory[] | undefined>;
    loadSearchResults: (params: { searchText: string }) => Promise<ICategory[] | undefined>;
    loadFilters?: (params: { categoryId?: string }) => Promise<IFilter[] | undefined>;
    onFiltersChange?: (
        filters: Record<string, string[]>,
        parentCategoryId?: string,
    ) => Promise<ICategory[] | undefined>;
}

const MODEL_SELECTION_SETTINGS: IModelSelectionSettings = {
    enableKeyFeatures: false,
    enableSubRanges: true,
    expandSubRanges: false,
    enableTechData: false,
    enablePrice: false,
    enableGroupAutoSkip: false,
    enableSkipToSubRange: false,
    hideSearchBar: false,
    enableFilters: true,
};

function ActionsCapture({
    actionsRef,
}: {
    actionsRef: MutableRefObject<IModelSelectionContext['actions'] | null>;
}) {
    const { actions } = useModelSelection();
    useEffect(() => {
        // eslint-disable-next-line no-param-reassign
        actionsRef.current = actions;
    }, [actionsRef, actions]);
    return null;
}

export function CatalogView({
    data,
    onProductClick,
    loadSubranges,
    loadSearchResults,
    loadFilters,
    onFiltersChange,
}: CatalogViewProps) {
    const actionsRef = useRef<IModelSelectionContext['actions'] | null>(null);

    const handleClick = (_event: MouseEvent<HTMLDivElement>, product: IProduct) => {
        onProductClick(product);
    };

    const hooks: IMSHooks = useMemo(
        () => ({
            onGroupClick: ({ group }) => {
                actionsRef.current?.setGroup(group);
                actionsRef.current?.setSearchTags([group]);
            },
            onGammaClick: ({ group, gamma }) => {
                if (gamma) {
                    actionsRef.current?.setGamma(gamma);
                    actionsRef.current?.setSearchTags(
                        [group, gamma].filter((tag): tag is ICategory => Boolean(tag)),
                    );
                }
            },
            onRangeClick: ({ group, gamma, range }) => {
                if (range) {
                    actionsRef.current?.setRange(range);
                    actionsRef.current?.setSearchTags(
                        [group, gamma, range].filter((tag): tag is ICategory => Boolean(tag)),
                    );
                }
            },
        }),
        [],
    );

    return (
        <ModelSelectionProvider
            data={data}
            settings={MODEL_SELECTION_SETTINGS}
            hooks={hooks}
            loadSubranges={loadSubranges}
            loadSearchResults={loadSearchResults}
            loadFilters={loadFilters}
            onFiltersChange={onFiltersChange}
        >
            <ActionsCapture actionsRef={actionsRef} />
            <CompareProductsProvider currency="">
                <ModelSelection
                    productCardProps={{
                        onClick: handleClick,
                        StartAction: SoonAvailabilityBadge,
                    }}
                />
            </CompareProductsProvider>
        </ModelSelectionProvider>
    );
}
